import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, History as HistoryIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sidebar } from "@/components/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useHistory } from "@/hooks/useHistory";
import { VideoCard } from "@/components/VideoCard";
import { Progress } from "@/components/ui/progress";

export default function History() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { history, isLoading, error } = useHistory();

  const handleLogout = async () => {
    await signOut();
    toast({ title: "已退出登录" });
    navigate("/");
  };

  const handleNavigate = (page: string) => {
    if (page === "home") navigate("/");
    else if (page === "vocabulary") navigate("/vocabulary");
    else if (page === "favorites") navigate("/favorites");
    else if (page === "history") navigate("/history");
    else if (page === "feedback") navigate("/feedback");
    else if (page === "profile") navigate("/profile");
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <HistoryIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">请先登录以查看学习记录</p>
            <Button onClick={() => navigate("/auth")}>去登录</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentPage="history"
        onNavigate={handleNavigate}
        isLoggedIn={!!user}
        onLogout={handleLogout}
      />

      <main className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
      )}>
        <div className="container px-6 py-8 max-w-6xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">学习记录</h1>
              <p className="text-sm text-muted-foreground">
                {isLoading ? "加载中..." : `共 ${history.length} 条学习记录`}
              </p>
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <Card>
              <CardContent className="pt-12 pb-12 text-center">
                <HistoryIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button
                  onClick={() => {
                    toast({ title: "请刷新页面重试" });
                  }}
                >
                  知道了
                </Button>
              </CardContent>
            </Card>
          ) : history.length === 0 ? (
            <Card>
              <CardContent className="pt-12 pb-12 text-center">
                <HistoryIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  还没有学习记录
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  观看视频时会自动保存进度，稍后可在这里继续学习
                </p>
                <Button onClick={() => navigate("/")}>
                  开始学习
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {history.map((record) => (
                <div key={record.id} className="relative group">
                  <VideoCard
                    video={record.video}
                    onClick={() => navigate(`/player/${record.video.id}`)}
                  />
                  <div className="mt-2 px-1">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>学习进度</span>
                      <span>{Math.round(record.progress || 0)}%</span>
                    </div>
                    <Progress value={record.progress || 0} className="h-1.5" />
                    <div className="text-xs text-muted-foreground mt-1 text-right">
                      上次学习: {new Date(record.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
